/**
 * REST helpers for the lobby browser. The actual room state is over Colyseus;
 * this module is just for the room list and faction catalogue.
 */

export interface PublicRoomSummary {
  roomId: string;
  name: string;
  players: number;
  maxPlayers: number;
  hasPassword: boolean;
  /** True for "coin secret" rooms (host clicked the private button).
   *  Surfaced in the list with a distinct iconchat so players can
   *  spot them at a glance. */
  isPrivate: boolean;
}

export interface FactionDef {
  id: string;
  name: string;
  color: string;
  /** Short cat-lore description used in tooltips / faction pickers. */
  lore?: string;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

/** Generic helpers reused by every page — surfaced so the admin
 *  routes (and future feature pages) don't have to redeclare them. */
export async function apiGet<T>(path: string): Promise<T> {
  return get<T>(path);
}
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error ?? ''; } catch { /* */ }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Admin user row — mirror of server `AdminUserRow`. */
export interface AdminUser {
  id: string;
  email: string;
  username: string;
  favoriteFactionId: string | null;
  shinies: number;
  gamesPlayed: number;
  gamesWon: number;
  rankedPoints: number;
  rankedGamesPlayed: number;
  rankedGamesWon: number;
  isAdmin: boolean;
  bannedAt: string | null;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface AdminUsersPage {
  page: number;
  pageSize: number;
  totalUsers: number;
  users: AdminUser[];
}

export type AdminFilter = 'all' | 'admins' | 'banned' | 'active' | 'dormant' | 'ranked';

export async function fetchAdminUsers(
  page: number,
  pageSize: number,
  q: string,
  sort: string,
  order: 'asc' | 'desc',
  filter: AdminFilter = 'all',
): Promise<AdminUsersPage> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sort,
    order,
    filter,
  });
  if (q) params.set('q', q);
  return apiGet<AdminUsersPage>(`/admin/users?${params.toString()}`);
}

export interface AdminStats {
  totalUsers: number;
  admins: number;
  banned: number;
  activeThisWeek: number;
  dormant: number;
  rankedPlayers: number;
  totalGames: number;
  totalRankedGames: number;
  totalShinies: number;
}
export async function fetchAdminStats(): Promise<AdminStats> {
  return apiGet<AdminStats>('/admin/stats');
}

export interface AdminGameRow {
  id: string;
  endedAt: string;
  isRanked: boolean;
  factionId: string | null;
  finishRank: number;
  participantCount: number;
  wasWinner: boolean;
  rankedDelta: number | null;
}
export async function fetchAdminUserGames(userId: string, limit = 15): Promise<AdminGameRow[]> {
  const r = await apiGet<{ games: AdminGameRow[] }>(`/admin/users/${userId}/games?limit=${limit}`);
  return r.games;
}

export async function adminResetRanked(userId: string): Promise<{
  rankedPoints: number;
  rankedGamesPlayed: number;
  rankedGamesWon: number;
}> {
  return apiPatch(`/admin/users/${userId}/reset-ranked`, {});
}

export async function adminPatchShinies(userId: string, delta: number): Promise<{ shinies: number }> {
  return apiPatch<{ shinies: number }>(`/admin/users/${userId}/shinies`, { delta });
}
export async function adminPatchBanned(userId: string, banned: boolean): Promise<{ bannedAt: string | null }> {
  return apiPatch<{ bannedAt: string | null }>(`/admin/users/${userId}/banned`, { banned });
}
export async function adminPatchAdmin(userId: string, isAdmin: boolean): Promise<{ isAdmin: boolean }> {
  return apiPatch<{ isAdmin: boolean }>(`/admin/users/${userId}/admin`, { isAdmin });
}

export async function fetchRooms(): Promise<PublicRoomSummary[]> {
  const r = await get<{ rooms: PublicRoomSummary[] }>('/rooms');
  return r.rooms;
}

/** Live counters shown on the home hub. */
export interface LobbiesStats {
  rankedPlaying: number;
  rankedSearching: number;
  publicLobbies: number;
  publicPlayers: number;
}

export async function fetchLobbiesStats(): Promise<LobbiesStats> {
  return get<LobbiesStats>('/lobbies/stats');
}

/** A room the logged-in user is still a participant of — used for the
 *  "Revenir à ma chasse" button at the top of the home hub. */
export interface MyActiveRoom {
  roomId: string;
  name: string;
  ranked: boolean;
  players: number;
  maxPlayers: number;
}

export async function fetchMyActiveRooms(): Promise<MyActiveRoom[]> {
  const r = await get<{ rooms: MyActiveRoom[] }>('/lobbies/mine');
  return r.rooms;
}

/** A game in progress that accepts spectators. */
export interface WatchableRoom {
  roomId: string;
  name: string;
  ranked: boolean;
  daily: boolean;
  hasPassword: boolean;
  participantCount: number;
  connected: number;
}

export async function fetchWatchableRooms(): Promise<WatchableRoom[]> {
  const r = await get<{ rooms: WatchableRoom[] }>('/lobbies/watch');
  return r.rooms;
}

/** Défi du jour board entry — mirror of server `DailyEntry`. */
export interface DailyEntry {
  rank: number;
  userId: string;
  username: string;
  score: number;
  won: boolean;
  turns: number;
  regions: number;
  factionId: string | null;
  isMe: boolean;
}

export interface DailyBoard {
  day: string;
  seed: number;
  botCount: number;
  played: boolean;
  me: DailyEntry | null;
  top: DailyEntry[];
}

export async function fetchDailyBoard(): Promise<DailyBoard> {
  return get<DailyBoard>('/daily');
}

let factionsCache: FactionDef[] | null = null;

export async function fetchFactions(): Promise<FactionDef[]> {
  if (factionsCache) return factionsCache;
  const r = await get<{ factions: FactionDef[] }>('/factions');
  factionsCache = r.factions;
  return factionsCache;
}

/** Virtual "barbare" faction injected client-side so in-game lookups
 *  (banner colour, head sprite) succeed for the NPC barbarian raider.
 *  NEVER returned by `fetchFactions()` so it doesn't show up in any
 *  faction picker (lobby, profile favorites, cosmetics). */
export const BARBARIAN_FACTION_DEF: FactionDef = {
  id: 'barbare',
  name: 'Barbares',
  color: '#7a2e1f',
  lore: 'Pillards venus d\'au-delà des mers — pas de roi, pas de pitié, juste des griffes.',
};

/** Same as `fetchFactions()` but appends the barbarian NPC faction at
 *  the end. Use this in any in-game screen that maps a participant's
 *  factionId to its colour / sprite, so the raid event's participant
 *  is rendered correctly. */
export async function fetchFactionsInGame(): Promise<FactionDef[]> {
  const base = await fetchFactions();
  // Avoid double-appending if a caller already injected it.
  if (base.some((f) => f.id === BARBARIAN_FACTION_DEF.id)) return base;
  return [...base, BARBARIAN_FACTION_DEF];
}

/** One entry in the "Insulter" submenu — id (stable), text (sent verbatim). */
export interface InsultDef { id: string; text: string }

let insultsCache: InsultDef[] | null = null;

export async function fetchInsults(): Promise<InsultDef[]> {
  if (insultsCache) return insultsCache;
  const r = await get<{ insults: InsultDef[] }>('/insults');
  insultsCache = r.insults;
  return insultsCache;
}
